import { SelectionModel } from '@angular/cdk/collections';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SortDirection } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import {
    ENucColumnSize,
    ENucColumnType,
    ENucSelectionMode,
    ENucSortOrder,
    INucSortOptionEvent,
    INucTableColumn,
    NUC_BUTTON_TYPE,
    NucButtonPrimaryComponent,
    NucDebounceValueChangedDirective,
    NucDialogConfigModel,
    NucDialogService,
    NucInputComponent,
    NucTableComponent
} from '@relayter/rubber-duck';
import { Subscription } from 'rxjs';
import { PermissionService } from '../../api/services/permission.service';
import { TeamService } from '../../api/services/team.service';
import { Toaster } from '../../classes/toaster.class';
import { PaginatorComponent } from '../../components/paginator/paginator.component';
import { PaginatorService } from '../../components/paginator/paginator.service';
import { PermissionModel } from '../../models/permission.model';
import { TeamModel, TeamPatchModel } from '../../models/response/team.model';

@Component({
    selector: 'om-team-permissions',
    templateUrl: './team-permissions.component.html',
    styleUrls: ['./team-permissions.component.scss'],
    providers: [PaginatorService],
    imports: [NucInputComponent, PaginatorComponent, NucDebounceValueChangedDirective, NucButtonPrimaryComponent, NucTableComponent]
})
export class TeamPermissionsComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly route = inject(ActivatedRoute);
    private readonly teamService = inject(TeamService);
    private readonly permissionService = inject(PermissionService);
    private readonly dialogService = inject(NucDialogService);
    private readonly paginatorService = inject(PaginatorService);

    public tableId = 'team-permissions';
    public teamName: string;
    public teamPermissions: PermissionModel[];
    public items: PermissionModel[] = [];
    public pageIndex: number;
    public pageSize: number;
    public total: number;
    public sortProperty: string;
    public sortOrder: SortDirection;
    public columns: INucTableColumn[] = [
        {
            title: 'Name',
            key: 'name',
            type: ENucColumnType.DEFAULT,
            sortProperty: 'name',
            size: ENucColumnSize.LARGE
        },
        {
            title: 'Description',
            key: 'description',
            type: ENucColumnType.DEFAULT,
            sortProperty: 'description',
            size: ENucColumnSize.LARGE
        }
    ];

    public ESelectionMode = ENucSelectionMode;
    public selection = new SelectionModel<PermissionModel>(true, [], false);
    public searchValue: string = '';
    public permissionsSubscription = new Subscription();
    public requestSubscription = new Subscription();
    private teamId: string;

    public ngOnInit(): void {
        this.teamId = this.route.snapshot.params.teamId;

        this.teamService
            .getTeam(this.teamId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((team) => {
                this.teamName = team.name;
                this.teamPermissions = team.permissions.map((permission) => new PermissionModel(permission));
                this.setSelectedPermissions();
                this.setPageIndex();
            });

        this.paginatorService
            .getPagination(this.tableId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((pagination) => {
                this.pageIndex = pagination.pageIndex;
                this.pageSize = pagination.pageSize;

                this.getPermissions();
            });
    }

    private getPermissions(): void {
        this.permissionsSubscription?.unsubscribe();
        this.permissionsSubscription = this.permissionService
            .getPermissions(this.pageSize, this.pageSize * (this.pageIndex - 1), this.sortProperty, this.sortOrder, this.searchValue)
            .subscribe({
                next: (results) => {
                    this.items = results.items;
                    this.total = results.total;
                },
                error: Toaster.handleApiError
            });
    }

    private setSelectedPermissions(): void {
        this.selection.clear();
        this.selection.select(...this.teamPermissions);
    }

    /**
     * Set page index, default the first page
     *
     * @param {number} [pageIndex]
     */
    public setPageIndex(pageIndex: number = 1): void {
        this.paginatorService.setPageIndex(this.tableId, pageIndex); // reset pageIndex
    }

    public onSortOptionChanged(event: INucSortOptionEvent): void {
        this.sortProperty = event.column?.sortProperty || '';
        if (event.sortOrder === ENucSortOrder.ASC) {
            this.sortOrder = event.column?.sortProperty ? 'asc' : '';
        } else {
            this.sortOrder = event.column?.sortProperty ? 'desc' : '';
        }

        this.setPageIndex();
    }

    /**
     * Check if the confirm-dialog needs to be shown on leaving the page
     * @return {boolean}
     */
    public canDeactivate(): boolean {
        if (!this.teamPermissions) {
            return true;
        }

        const selectedValues = this.selection.selected;

        return (
            selectedValues.every((permission) => this.teamPermissions.find((teamPermission) => teamPermission._id === permission._id)) &&
            this.teamPermissions.every((teamPermission) => selectedValues.find((selected) => selected._id === teamPermission._id))
        );
    }

    public openConfirmDialog(): void {
        const confirmDialogConfig = new NucDialogConfigModel('Update permissions', 'You are about to change permissions for the team. Are you sure?');
        const confirmDialog = this.dialogService.openDialog(confirmDialogConfig);
        confirmDialogConfig.addAction('Cancel', NUC_BUTTON_TYPE.SECONDARY).subscribe(() => confirmDialog.close());
        confirmDialogConfig.addAction('Yes', NUC_BUTTON_TYPE.PRIMARY).subscribe(() => {
            confirmDialog.close();
            this.updateTeamPermissions();
        });
    }

    /**
     * Puts all buffered permission changes
     * Clears permission change buffer on success
     */
    private updateTeamPermissions(): void {
        this.requestSubscription = this.teamService
            .patchTeam(this.teamId, { permissions: this.selection.selected.map((permission) => permission._id) } as TeamPatchModel)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (team: TeamModel) => {
                    this.teamPermissions = team.permissions.map((permission) => new PermissionModel(permission));
                    this.setSelectedPermissions();
                    Toaster.success('Successfully updated permissions for the team');
                },
                error: Toaster.handleApiError
            });
    }
}
